import { createAsyncThunk } from '@reduxjs/toolkit';
import { AccountRepository } from 'repositories/AccountRepository';
import { EPayoutStatus } from 'types';
import { EApiResponseStatus } from 'types/api';
import { ISignInResponseData } from 'types/auth';
import { IFetchUserResponse, ISignInResponse, ISignInUserData } from 'types/users';
import { removeToken, setToken } from 'utils';

export const fetchUser = createAsyncThunk<IFetchUserResponse, string>(
  'auth/fetchUser',
  async (token: string) => {
    try {
      const response = await AccountRepository.fetchUser(token);
      return response;
    } catch (error) {
      console.error('signIn error', error);
      if (error instanceof Error) {
        return { status: EApiResponseStatus.FAILURE, error: error.message };
      }
      return { status: EApiResponseStatus.FAILURE, error: 'unknown error' };
    }
  },
);

export const signIn = createAsyncThunk<ISignInResponseData, { userData: ISignInUserData }>(
  'auth/signIn',
  async ({ userData }, { dispatch, rejectWithValue }) => {
    const { password, name, isRemember } = userData;
    const response = await AccountRepository.signIn({ password, name });
    if (response.status === EApiResponseStatus.FAILURE || !response.data) {
      return rejectWithValue(response);
    }
    if (!response.data.twoFARequired) {
        dispatch(fetchUser(response.data.token));
        if (isRemember) setToken(response.data.token);
    } else {
        setToken(response.data.token);
    }
    return response.data;
  },
);

export const signOut = createAsyncThunk('auth/signOut', async () => {
  removeToken();
});

export const verify2FA = createAsyncThunk<ISignInResponseData, { token: string; code: string; isRemember?: boolean }>(
  'auth/verify2FA',
  async ({ token, code, isRemember }, { dispatch, getState }) => {
    const response = await AccountRepository.verify2FA(token, code);
    if (response.status === EApiResponseStatus.FAILURE || !response.data) {
      throw Error(response.error || 'Invalid code');
    }
    dispatch(fetchUser(response.data.token));

    const state = getState() as { auth: { isRemember: boolean } };
    const finalIsRemember = isRemember !== undefined ? isRemember : state.auth.isRemember;

    if (finalIsRemember) setToken(response.data.token);
    return response.data;
  },
);

export const resend2FA = createAsyncThunk<ISignInResponse, { token: string }>(
  'auth/resend2FA',
  async ({ token }) => {
    const response = await AccountRepository.resend2FA(token);
    if (response.status === EApiResponseStatus.FAILURE) {
      throw Error(response.error);
    }
    return response;
  },
);

export const get2FATimeout = createAsyncThunk<{ timeout: number }, { token: string }>(
  'auth/get2FATimeout',
  async ({ token }) => {
    const response = await AccountRepository.get2FATimeout(token);
    if (response.status === EApiResponseStatus.FAILURE) {
      throw Error(response.error);
    }
    return { timeout: response.timeout || 0 };
  },
);
